import { useEffect, useState, type RefObject } from 'react';

/**
 * Returns a max-height that bounds `ref`'s element to the space left between
 * its top edge and the bottom of the window, minus `reserve` pixels (a pinned
 * pagination bar, say). Scrolling content inside that element then keeps a
 * sticky header frozen and its horizontal scrollbar on screen, instead of the
 * page growing and the scrollbar sitting at the far end of it.
 *
 * Pass `reserve: undefined` to opt out — the hook then returns undefined.
 */
export function useFillViewportHeight(
  ref: RefObject<HTMLElement | null>,
  reserve: number | undefined,
  min = 280,
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (reserve === undefined) return;
    const el = ref.current;
    if (!el) return;
    const recompute = () => {
      // getBoundingClientRect().top is the distance from the viewport top, so
      // this re-measures correctly however tall the filters above happen to be.
      const top = el.getBoundingClientRect().top;
      setMaxHeight(Math.max(min, window.innerHeight - top - reserve));
    };
    // ResizeObserver fires once on observe(), which supplies the initial
    // measurement without a synchronous setState in the effect body. Watching
    // <body> also catches the filter row wrapping and pushing the table down.
    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [ref, reserve, min]);

  return maxHeight;
}
